import { CRMContact, CRMAccount } from './crm';

export type AppointmentStatus = 'scheduled' | 'confirmed' | 'cancelled' | 'completed' | 'no_show';

export type AppointmentTypeEnum = 'discovery' | 'demo' | 'onboarding' | 'support' | 'followup' | 'other';

export interface AppointmentType {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  duration_minutes: number;
  color: string;
  is_active: boolean;
  requires_confirmation: boolean;
  buffer_before: number;
  buffer_after: number;
  max_bookings_per_day: number | null;
  available_days: number[];
  available_hours_start: string;
  available_hours_end: string;
  owner_id: string | null;
  is_public: boolean;
  associated_stage_id: string | null;
  advance_on_create: boolean;
  advance_on_complete: boolean;
  stage_on_create_id: string | null;
  stage_on_complete_id: string | null;
  default_location: string | null;
  calendar_event_title: string | null;
  calendar_event_description: string | null;
  cc_emails: string[];
  created_at: string;
  updated_at: string;
}

export interface AvailabilityRule {
  id: string;
  owner_id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  appointment_type_id: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Appointment {
  id: string;
  title: string;
  description: string | null;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  status: AppointmentStatus;
  type: AppointmentTypeEnum;
  appointment_type_id: string | null;
  location: string | null;
  google_event_id: string | null;
  google_meet_link: string | null;
  reschedule_token?: string | null;
  contact_id: string;
  account_id: string | null;
  opportunity_id: string | null;
  onboarding_lead_id: string | null;
  owner_id: string | null;
  reminder_sent: boolean;
  reminder_at: string | null;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  meeting_notes: string | null;
  additional_attendees: string[];
  created_at: string;
  updated_at: string;
  contact?: CRMContact;
  account?: CRMAccount;
  appointment_type?: AppointmentType;
}

export interface TimeSlot {
  start: string;
  end: string;
  is_off_hours?: boolean;
}

export interface CreateProspectInput {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  company_name?: string;
  /** Set when the user links the new contact to an existing account (anti-duplicate). */
  account_id?: string;
}

export interface BookAppointmentInput {
  appointment_type_id: string;
  start_time: string;
  contact_id?: string;
  create_prospect?: CreateProspectInput;
  notes?: string;
  opportunity_id?: string;
  onboarding_lead_id?: string;
  additional_attendees?: string[];
  /** "Autre" type only: custom title/duration entered on the fly. */
  custom_title?: string;
  custom_duration_minutes?: number;
}

export interface AppointmentFilters {
  date_from?: string;
  date_to?: string;
  status?: AppointmentStatus;
  contact_id?: string;
  include_cancelled?: boolean;
}

export type CalendarViewMode = 'month' | 'week' | 'day' | 'list';

export const APPOINTMENT_STATUS_LABELS: Record<AppointmentStatus, string> = {
  scheduled: 'Planifie',
  confirmed: 'Confirme',
  cancelled: 'Annule',
  completed: 'Termine',
  no_show: 'Absent',
};

export const APPOINTMENT_STATUS_COLORS: Record<AppointmentStatus, string> = {
  scheduled: 'bg-blue-100 text-blue-800',
  confirmed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-neutral-100 text-neutral-800',
  completed: 'bg-emerald-100 text-emerald-800',
  no_show: 'bg-red-100 text-red-800',
};

export const APPOINTMENT_TYPE_LABELS: Record<AppointmentTypeEnum, string> = {
  discovery: 'Decouverte',
  demo: 'Demo',
  onboarding: 'Onboarding',
  support: 'Support',
  followup: 'Suivi',
  other: 'Autre',
};

export const DAY_LABELS: Record<number, string> = {
  0: 'Lundi',
  1: 'Mardi',
  2: 'Mercredi',
  3: 'Jeudi',
  4: 'Vendredi',
  5: 'Samedi',
  6: 'Dimanche',
};

export const LOCATION_OPTIONS = [
  { value: 'Google Meet', label: 'Google Meet' },
  { value: 'Zoom', label: 'Zoom' },
  { value: 'Teams', label: 'Microsoft Teams' },
  { value: 'Phone', label: 'Telephone' },
  { value: 'In Person', label: 'En personne' },
  { value: 'Other', label: 'Autre' },
];
